/**
 * UBER CRITICAL UPDATE - 8/7/2025
 * Fixed excessive re-rendering issue in useEffect (lines 978-1005)
 * Changed: Simplified Firebase listener update conditions to only trigger on conversation changes
 * Previous: Length comparisons were causing re-renders during message streaming
 * Impact: Massive performance improvement, eliminated constant re-rendering
 * Revert: git checkout d0e0cb9 -- frontend/src/components/chatWindow.js
 */

import { auth } from '../firebase';
import React, { useState, useRef, useCallback, useEffect, memo, useReducer } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useScrollOrchestrator } from './chat/hooks/useScrollOrchestrator';
import StreamingMessage from './chat/StreamingMessage';
import { useStickyBanner, MOBILE_BANNER_HEIGHT } from './chat/MobileTopBannerSticky';
import UnifiedIndicatorBar, { 
  useIndicatorSystem, 
  RemainingMessagesIndicator as RemainingMessagesHelper,
  LoadingStateIndicator 
} from './chat/UnifiedIndicatorBar';

// Component imports
import SEOHead from './SEO/SEOHead';
import MessageInput from "./chat/components/MessageInput";
import { config } from '../config';
import { useUsageLimiter } from '../utils/usageLimiter';
import UpgradeNotice from './upgradeNotice';
import { useMessageHandlers } from './chatWindowMsgHandler';
import ChatHistoryPanel from './auxiliary/ChatHistoryPanel';
import MobileStatsPanel from './auxiliary/mobileStatsPanel';
import { saveMessage } from '../utils/db-messaging';
import { useConversations } from '../hooks/useConversations';
import { useMessageHistory } from '../hooks/useMessageStorage';
import ScrollToBottomButton from './ui/ScrollToBottomButton';
import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
import { incrementCopyCount } from '../services/userCopyTracker';
import TopChatBanner from './TopChatBanner/TopChatBanner';
import AddToBetSlipButton from './virtualBetSlip/AddToBetSlipButton';
// import PickExtractorButton from './virtualBetSlip/PickExtractorButton';
// import RemainingMessagesIndicator from "./Indicators/RemainingMessagesIndicator";
import ChatSuggestionChips from './ChatChips';
import { useGamePrediction } from '../hooks/useGamePrediction';
import GamePredictionModal from '../components/gamePrediction/GamePredictionModal';
import FloatingPromptAssistant from './FloatingPromptAssistant';
import { useMobileViewport } from '../hooks/useMobileViewport';
import ChatOnboardingTour from './chat/Onboarding/ChatOnboardingTour'; // Onboarding tour component
import { useMessageLimitWarning } from './chat/hooks/useMessageLimitWarning';
import { useFloatingPrompt } from './chat/hooks/useFloatingPrompt';
import { useUserStats } from './chat/hooks/useUserStats';
import { useMobileLayout } from './chat/hooks/useMobileLayout';
import { useMessagePersistence } from './chat/hooks/useMessagePersistence';
import { useResponseMode } from './chat/hooks/useResponseMode';
import { useNavigation } from './context/NavigationContext';
import { WELCOME_MESSAGE } from './chat/constants/messages';
import LoadingIndicator from './chat/components/LoadingIndicator';
import MessageLimitModal from './chat/components/MessageLimitModal';
import MessageItem from './chat/components/MessageItem';


// CSS Styles (restored from old version)
const chatWindowStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  .animate-pulse {
    animation: pulse 1.5s infinite;
  }
`;

// TODO: ChatContext is created but never provided or used in this file.
// Consider either:
// 1. Delete if not needed (YAGNI principle)
// 2. Implement <ChatContext.Provider> if you want to share uiState with deeply nested children
// Note: Different from /chat/chatContext.js which handles app-to-chat communication
const ChatContext = React.createContext();

// Enhanced UI State Management with useReducer (SAFE - doesn't affect streaming)
const uiStateReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_STATE':
      return { ...state, ...action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_SCROLL_BUTTON':
      return { ...state, showScrollButton: action.payload };
    case 'SET_UPGRADE_PROMPT':
      return { ...state, showUpgradePrompt: action.payload };
    case 'SET_REMAINING_MESSAGES_INDICATOR':
      return { ...state, showRemainingMessages: action.payload };
    case 'RESET_UI':
      return {
        ...state,
        isLoading: false,
        error: null,
        showUpgradePrompt: false,
        isAtBottom: true,
        showScrollButton: false
      };
    default:
      return state;
  }
};



// Main ChatWindow Component with your original useState pattern + UI enhancements
const ChatWindow = memo(({
  initialMessages = [],
  isCompact = false,
  onUpgrade,
  userId,
  isStreaming = false,
  activeConversationProp = null,
  initialTransitionComplete = false,
  startWithBlankChat = false,
  onNewChat
}) => {
  // ORIGINAL useState pattern (CRITICAL for streaming)
  // Add welcome message if starting with empty messages
  const getInitialMessages = () => {
    if (initialMessages.length === 0) {
      return [{
        id: 'welcome-msg',
        text: WELCOME_MESSAGE,
        sender: 'assistant',
        timestamp: new Date()
      }];
    }
    return initialMessages;
  };

  const [messages, setMessages] = useState(getInitialMessages());
  const [isFirstMessage, setIsFirstMessage] = useState(initialMessages.length === 0);
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Enhanced UI state management (SAFE - doesn't affect streaming)
  const [uiState, uiDispatch] = useReducer(uiStateReducer, {
    isLoading: false,
    isTransitioning: false,
    error: null,
    showUpgradePrompt: false,
    showScrollButton: false,
    isAtBottom: true,
    showResponseToggles: false,
    showRemainingMessages: true
  });

  // Important refs for managing the component lifecycle
  const isMountedRef = useRef(true);
  const initialLoadCompleteRef = useRef(false);
  const isStreamingRef = useRef(isStreaming);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null); // For ScrollOrchestrator
  const viewTransitionCompleteRef = useRef(initialTransitionComplete);
  const messagesProcessedRef = useRef(false);
  const subscriptionDeferTimerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const prevInitialMessagesRef = useRef(initialMessages);
  const isLoadingHistoryRef = useRef(false);
  const chatOnboardingRef = useRef(null); // Ref for controlling the onboarding tour
  
  // Hooks
  const usageLimiter = useUsageLimiter(userId);
  const { tier } = usageLimiter;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Mobile viewport and keyboard handling
  const { 
    viewport, 
    deviceInfo, 
    isKeyboardVisible, 
    keyboardHeight,
    getMobileInputStyles, 
    getContentAreaStyles,
    getFloatingButtonStyles,
    safeArea
  } = useMobileViewport();

  // ScrollOrchestrator for world-class scroll behavior
  const {
    snapshotBeforeMutate,
    adjustAfterPrepend,
    maybeStickToBottomOnAppend,
    throttledStickToBottom,
    jumpToBottom,
    smoothToBottom,
    temporarilyDisableSmoothScrolling,
    isAtBottomRef
  } = useScrollOrchestrator(scrollContainerRef, {
    bottomThreshold: 5,
    enableDebugLogging: false
  });

  // Sticky banner management for mobile
  const {
    bannerHeight,
    getContentOffset
  } = useStickyBanner();

  // Unified indicator system
  const {
    activeIndicator,
    showIndicator,
    hideIndicator,
    clearAllIndicators
  } = useIndicatorSystem();

  // Atomic UI state updates (enhanced) - CONSISTENT PATTERN
  const updateUIState = useCallback((updates) => {
    if (!isMountedRef.current) return;
    uiDispatch({ type: 'UPDATE_STATE', payload: updates });
  }, []);

  // Auth debugging effect (restored from old version)
  useEffect(() => {
    console.log("Auth state:", {
      currentUser: auth.currentUser,
      email: auth.currentUser?.email,
      uid: auth.currentUser?.uid,
      userId: userId
    });
  }, [userId]);

  // Use the externally provided activeConversation first
  const externalActiveConversationRef = useRef(activeConversationProp);
  useEffect(() => {
    externalActiveConversationRef.current = activeConversationProp;
  }, [activeConversationProp]);

  // Only use conversations hook if needed
  const { 
    conversations,
    activeConversation: hookActiveConversation,
    loading: conversationsLoading,
    error: conversationsError,
    selectConversation
  } = useConversations(userId);

  // Determine which active conversation to use
  const effectiveActiveConversation = externalActiveConversationRef.current || hookActiveConversation;

  // Response mode management (must be before useMessageHandlers)
  const { responseMode, setResponseMode, getModeSuffix, RESPONSE_MODES } = useResponseMode('normal');

  // Get message handlers with ORIGINAL dependencies - FIXED TO USE updateUIState
  const {
    handleFirstMessage: originalHandleFirstMessage,
    handleNewMessage: originalHandleNewMessage,
    filterDuplicateMessages,
    isStreaming: isHandlerStreaming
  } = useMessageHandlers({
    userId,
    messages, // ORIGINAL useState
    setMessages, // ORIGINAL useState
    setIsFirstMessage, // ORIGINAL useState
    setIsLoading: (isLoading) => updateUIState({ isLoading }), // FIXED: consistent pattern
    setError: (error) => updateUIState({ error }), // FIXED: consistent pattern
    checkLimit: usageLimiter.checkLimit,
    canSendMessage: usageLimiter.canSendMessage,
    setShowUpgradePrompt: (show) => updateUIState({ showUpgradePrompt: show }), // FIXED: consistent pattern
    incrementUsage: usageLimiter.incrementUsage,
    activeConversation: effectiveActiveConversation,
    selectConversation,
    responseMode
  });

  // Get message history using the filter function
  const { 
    messages: conversationMessages, 
    updateMessages: setConversationMessages,
    loading: messageHistoryLoading, 
    error: messageHistoryError 
  } = useMessageHistory(userId, effectiveActiveConversation?.id, filterDuplicateMessages);

  // Message persistence (sessionStorage)
  const { saveState: saveSessionState, clearState: clearSessionState } = useMessagePersistence({
    userId,
    messages,
    activeConversation: effectiveActiveConversation,
    isStreaming: isStreamingRef.current || isStreaming,
    hasProcessedMessages: messagesProcessedRef.current,
    location,
    onRestore: useCallback(({ messages: restoredMessages, activeConversation: restoredConversation, isFirstMessage, initialTransitionComplete }) => {
      if (restoredMessages) {
        setMessages(restoredMessages);
        setIsFirstMessage(isFirstMessage !== undefined ? isFirstMessage : false);
      }
      if (restoredConversation) {
        setActiveConversationId(restoredConversation.id);
        selectConversation(restoredConversation);
      }
      if (initialTransitionComplete) {
        updateUIState({ initialTransitionComplete: true });
      }
    }, [setMessages, setIsFirstMessage, selectConversation, updateUIState])
  });

  // State for history handling
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Combine loading and error states
  useEffect(() => {
    setHistoryLoading(messageHistoryLoading);
    if (messageHistoryError) {
      setHistoryError(messageHistoryError);
    }
  }, [messageHistoryLoading, messageHistoryError]);

  // Update messages when initialMessages changes (critical for streaming updates)
  useEffect(() => {
    // Only update if we have new messages and they're different
    if (initialMessages.length > 0 && initialMessages !== prevInitialMessagesRef.current) {
      // Quick length check first (avoids expensive JSON.stringify)
      if (prevInitialMessagesRef.current.length !== initialMessages.length) {
        setMessages(initialMessages);
        prevInitialMessagesRef.current = initialMessages;
      } else {
        // Only do expensive comparison if lengths are the same
        const lastNewMsg = initialMessages[initialMessages.length - 1];
        const lastPrevMsg = prevInitialMessagesRef.current[prevInitialMessagesRef.current.length - 1];
        
        // Compare last message to detect streaming updates
        if (lastNewMsg?.id !== lastPrevMsg?.id || lastNewMsg?.text !== lastPrevMsg?.text) {
          setMessages(initialMessages);
          prevInitialMessagesRef.current = initialMessages;
        }
      }
    }
  }, [initialMessages]);

  // Modified conversation messages effect with filtering logic
  useEffect(() => {
    if (effectiveActiveConversation) {
      setActiveConversationId(effectiveActiveConversation.id);
      
      if (conversationMessages.length > 0) {
        // Only update if we're not streaming AND this is a different conversation
        // OR if the message IDs are completely different (not just count)
        const isDifferentConversation = messages.length > 0 && conversationMessages.length > 0 && 
                                       messages[0].id !== conversationMessages[0].id;
        const shouldUpdate = !isStreamingRef.current && 
                           !isHandlerStreaming() && 
                           isDifferentConversation;
        
        if (shouldUpdate) {
          console.log('Loading messages from Firestore:', conversationMessages.length);
          setMessages(conversationMessages);
          setIsFirstMessage(false);
          // Note: sessionStorage save is now handled by useMessagePersistence hook
        }
      }
    }
  }, [effectiveActiveConversation, conversationMessages, messages, userId, setIsFirstMessage, isHandlerStreaming]);

  // Clean up subscriptions and effects on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    // Add chat-interface class to body for proper mobile handling
    document.body.classList.add('chat-interface');
    
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (subscriptionDeferTimerRef.current) {
        clearTimeout(subscriptionDeferTimerRef.current);
      }
      
      // Remove chat-interface class on unmount
      document.body.classList.remove('chat-interface');
    };
  }, []);
  
  // Handle streaming state changes
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);
  
  // Handle transition state changes
  useEffect(() => {
    viewTransitionCompleteRef.current = initialTransitionComplete;
    
    if (initialTransitionComplete && !initialLoadCompleteRef.current) {
      initialLoadCompleteRef.current = true;
    }
  }, [initialTransitionComplete]);

  // Viewport height adjustment
  useEffect(() => {
    const setViewHeight = () => {
      if (containerRef.current) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
    };
    setViewHeight();
    let timeoutId = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(setViewHeight, 150);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', setViewHeight);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', setViewHeight);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Initialize with initial messages
  useEffect(() => {
    if (initialMessages.length > 0 && !messagesProcessedRef.current) {
      setMessages(initialMessages);
      messagesProcessedRef.current = true;
    }
  }, [initialMessages]);

  // Note: Session storage handling is now managed by useMessagePersistence hook

  // Handle chat selection
  const handleChatSelect = useCallback(async (selectedConversation) => {
    setMessages([]);
    messagesProcessedRef.current = true; // Mark as processed to prevent sessionStorage override
    isStreamingRef.current = false;
    setIsFirstMessage(false);
    
    updateUIState({
      isLoading: false,
      streamingResponse: false
    });
    
    await selectConversation(selectedConversation);
    
    try {
      updateUIState({ isLoading: true });
      const { fetchMessagesForConversation } = await import('../utils/db-messaging');
      const conversationMessages = await fetchMessagesForConversation(userId, selectedConversation.id);
      
      if (isMountedRef.current) {
        setMessages(conversationMessages);

        // Save to sessionStorage via hook
        saveSessionState({
          messages: conversationMessages,
          conversationId: selectedConversation.id
        });

        updateUIState({ isLoading: false });
      }
    } catch (err) {
      console.error("Error loading selected conversation:", err);
      if (isMountedRef.current) {
        updateUIState({ 
          isLoading: false,
          error: "Error loading conversation"
        });
      }
    }
  }, [userId, selectConversation, updateUIState]);

  const handleNewChatTest = useCallback(() => {
    clearSessionState(); // Clear via hook
    // Add welcome message for new chat
    setMessages([{
      id: 'welcome-msg',
      text: WELCOME_MESSAGE,
      sender: 'assistant',
      timestamp: new Date()
    }]);
    setIsFirstMessage(true);
    selectConversation(null);

    if (onNewChat) {
      onNewChat();
    }

    // Scroll to top for new chat
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [onNewChat, selectConversation, clearSessionState]);

  // Handle reactions
  const handleReaction = useCallback(async (messageId, reaction) => {
    try {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, reaction } : msg
        )
      );
      if (reaction === "thumbsUp") {
        await updatePointsOnMessage(userId, true);
      }
    } catch (error) {
      console.error("Error handling reaction:", error);
    }
  }, [userId]);

  // Handler for suggestion clicks
  const handleSuggestionClick = useCallback(async (prompt) => {
    if (uiState.isLoading || isStreamingRef.current || isStreaming) return;
    
    const message = {
      text: prompt,
      sender: "user"
    };
    
    if (isFirstMessage) {
      await originalHandleFirstMessage(message);
    } else {
      await originalHandleNewMessage(message, responseMode);
    }
  }, [
    isFirstMessage, 
    originalHandleFirstMessage, 
    originalHandleNewMessage, 
    uiState.responseMode, 
    uiState.isLoading, 
    isStreaming
  ]);

  // Handler for help button click - triggers onboarding tour
  const handleHelpClick = useCallback(() => {
    if (chatOnboardingRef.current) {
      chatOnboardingRef.current.start(); // Start the onboarding tour when help button clicked
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    smoothToBottom();
    updateUIState({ showScrollButton: false });
  }, [smoothToBottom, updateUIState]);

  // Auto-scroll removed - users control their own scroll position
  // They can use the scroll-to-bottom button when needed

  // Game prediction
  const {
    predictionState,
    updatePredictionState,
    gameUsageState,
    runAutomatedGamePrediction
  } = useGamePrediction({
    userId,
    originalHandleFirstMessage,
    originalHandleNewMessage,
    updateUIState,
    tier,
    messages,
    setMessages
  });

  // Message limit warning (long conversation prompt)
  const {
    shouldShowWarning: showMessageLimitWarning,
    messageCount: warningMessageCount,
    handleContinue: handleMessageLimitContinue,
    handleNewChat: handleMessageLimitNewChat,
    handleClose: handleMessageLimitClose
  } = useMessageLimitWarning({
    conversationId: effectiveActiveConversation?.id || null,
    messageCount: effectiveActiveConversation?.metadata?.messageCount || 0,
    isStreaming: isStreamingRef.current || isStreaming,
    isLoading: uiState.isLoading,
    onNewChat: handleNewChatTest
  });

  // Floating prompt assistant
  const {
    isVisible: floatingPromptVisible,
    handleClose: handleFloatingPromptClose
  } = useFloatingPrompt({
    messages,
    isStreaming: isStreamingRef.current || isStreaming
  });

  // User statistics
  const { stats: userStats } = useUserStats({ userId });

  // Mobile layout and panel visibility
  const {
    isMobile,
    showMobileStats,
    showChatList,
    isInputCollapsed,
    toggleMobileStats,
    toggleChatList,
    toggleInputCollapse,
    closeMobileStats,
    closeChatList
  } = useMobileLayout();

  // Navigation context for mobile banner control
  const { setMobileBannerComponent } = useNavigation();

  // Set mobile banner to 'none' when in chat (chatWindow handles its own banner)
  useEffect(() => {
    if (isMobile) {
      setMobileBannerComponent('none');
    }
    return () => {
      // Reset to default when leaving chat
      setMobileBannerComponent('default');
    };
  }, [isMobile, setMobileBannerComponent]);

  const handleNewMessage = useCallback(async (message) => {
    isStreamingRef.current = true;
    updateUIState({ isLoading: true }); // FIXED: consistent pattern
    const loadingId = showIndicator('LOADING', 'Processing your message...');
    try {
      await originalHandleNewMessage(message, responseMode);
    } finally {
      setTimeout(() => {
        isStreamingRef.current = false;
        updateUIState({ isLoading: false }); // FIXED: consistent pattern
        hideIndicator(loadingId);
      }, 500);
    }
  }, [originalHandleNewMessage, responseMode, updateUIState, showIndicator, hideIndicator]);

  // Handle upgrade prompt display - FIXED: consistent pattern
  useEffect(() => {
    if (usageLimiter.canSendMessage === false && 
        (usageLimiter.tier === 'basic' || usageLimiter.tier === 'pro')) {
      updateUIState({ showUpgradePrompt: true }); // FIXED: consistent pattern
    }
  }, [usageLimiter.canSendMessage, usageLimiter.tier, updateUIState]);

  // Handle streaming height changes
  const handleStreamingHeightGrow = useCallback(() => {
    if (isAtBottomRef.current && isStreamingRef.current) {
      throttledStickToBottom({ preferSmooth: false });
    }
  }, [throttledStickToBottom, isAtBottomRef]);

  // Watch for new messages and auto-scroll if at bottom
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Auto-scroll only if user is at bottom
      if (isAtBottomRef.current) {
        maybeStickToBottomOnAppend({ 
          preferSmooth: !isStreamingRef.current 
        });
      }
    }
  }, [messages.length, maybeStickToBottomOnAppend, isAtBottomRef]);

  // Update scroll button visibility based on scroll position
  useEffect(() => {
    updateUIState({ 
      showScrollButton: !isAtBottomRef.current && messages.length > 3 
    });
  }, [messages.length, updateUIState, isAtBottomRef]);

  // Temporarily disable smooth scrolling during keyboard transitions
  useEffect(() => {
    if (scrollContainerRef.current) {
      temporarilyDisableSmoothScrolling();
    }
  }, [isKeyboardVisible, temporarilyDisableSmoothScrolling]);

  // Auto-hide the indicator after some time
  useEffect(() => {
    if (usageLimiter.tier === 'basic' && usageLimiter.canSendMessage) {
      updateUIState({ showRemainingMessages: true });
      const timer = setTimeout(() => {
        updateUIState({ showRemainingMessages: false });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [usageLimiter.tier, usageLimiter.canSendMessage, activeConversationId]);

  // Effect to handle starting with a blank chat
  useEffect(() => {
    if (startWithBlankChat) {
      // Add welcome message for blank chat
      setMessages([{
        id: 'welcome-msg',
        text: WELCOME_MESSAGE,
        sender: 'assistant',
        timestamp: new Date()
      }]);
      setIsFirstMessage(true);
      selectConversation(null);
      clearSessionState(); // Clear via hook
      messagesProcessedRef.current = false;
      isStreamingRef.current = false;

      // Scroll to top for blank chat
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [startWithBlankChat, selectConversation, clearSessionState]);


  return (
    <>
      <SEOHead />
      <div className="flex flex-col h-screen bg-gray-950 overflow-x-hidden" ref={containerRef}>
        {/* Optimized background for better readability */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div 
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse at top right, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
                radial-gradient(ellipse at bottom left, rgba(16, 185, 129, 0.04) 0%, transparent 50%),
                linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)
              `
            }}
          />
        </div>
      <TopChatBanner
        userId={userId}
        userStats={userStats}
        responseMode={responseMode}
        setResponseMode={setResponseMode}
        updateUIState={updateUIState}
        RESPONSE_MODES={RESPONSE_MODES}
        handleNewChat={handleNewChatTest}
        onSuggestionClick={handleSuggestionClick}
        onShowMobileStats={toggleMobileStats}
        onHelpClick={handleHelpClick} // Pass help handler for onboarding trigger
        isMobile={isMobile}
        tier={tier}
        gameUsageState={gameUsageState}
        runAutomatedGamePrediction={runAutomatedGamePrediction}
        // Pass full usageLimiter for any custom prompt functionality
        usageLimiter={usageLimiter}
      />
  
      {/* Main Chat Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Chat History Panel */}
        <div className="hidden md:block">
          <ChatHistoryPanel
              userId={userId}
              activeConversationId={effectiveActiveConversation?.id}
              onChatSelect={handleChatSelect}
              onNewChat={handleNewChatTest}
            />
        </div>

        <div className="flex-1 flex flex-col relative">
          {/* Unified Indicator Bar - Below sticky banner */}
          <UnifiedIndicatorBar 
            indicator={activeIndicator}
            onDismiss={hideIndicator}
            position="below-banner"
            isDesktop={!isMobile}
          />
          
          {/* Helper components for automatic indicator management */}
          <RemainingMessagesHelper 
            remainingMessages={usageLimiter.remainingMessages}
            showIndicator={showIndicator}
            isPro={usageLimiter.tier === 'pro'}
            isInitializing={usageLimiter.isInitializing}
          />
          
          <LoadingStateIndicator
            isLoading={uiState.isLoading || isStreamingRef.current || isStreaming}
            loadingText={isStreamingRef.current ? "Generating response..." : "Processing..."}
            showIndicator={showIndicator}
            hideIndicator={hideIndicator}
          />
  
          <div className={`flex-1 overflow-hidden ${isMobile ? 'px-0' : 'px-4'} py-4 bg-black/20 backdrop-blur-sm ${isMobile ? "pb-24" : ""} relative`}>
            <div className={`${isMobile ? 'w-full' : 'max-w-3xl mx-auto'} h-full`}>

              {/* Native scrolling message list with ScrollOrchestrator */}
              <div
                ref={scrollContainerRef}
                className="message-list-container flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
                style={{
                  // Account for sticky banner height on mobile
                  height: isMobile 
                    ? `calc(var(--vh, 1vh) * 100 - ${getContentOffset ? getContentOffset() : MOBILE_BANNER_HEIGHT}px - 80px)`
                    : 'calc(var(--vh, 1vh) * 100 - 180px)',
                  // Add top padding on mobile to ensure content starts below banner (NOT indicator)
                  paddingTop: isMobile 
                    ? `${getContentOffset ? getContentOffset() : MOBILE_BANNER_HEIGHT}px` 
                    : 0,
                  paddingBottom: isKeyboardVisible 
                    ? `${keyboardHeight + safeArea.bottom + 80}px`
                    : `${safeArea.bottom + 80}px`,
                  // Smooth transition when keyboard appears
                  transition: 'padding-bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <div className={`message-list ${isMobile ? 'px-2' : 'px-4'} py-2`}>
                  {/* Dynamic spacer at the top when indicator is visible */}
                  <div 
                    className="indicator-spacer"
                    style={{ 
                      height: activeIndicator && isMobile ? 40 : 0,
                      transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      flexShrink: 0
                    }}
                    aria-hidden="true"
                  />
                  
                  {messages.map((msg, index) => {
                    const isLastMessage = index === messages.length - 1;
                    const isStreamingMessage = isLastMessage && isStreamingRef.current && msg.sender === 'assistant';
                    
                    if (isStreamingMessage) {
                      // Use StreamingMessage for the currently streaming message
                      return (
                        <div key={msg.id || `msg-${index}`} className="flex justify-start py-1.5 w-full">
                          <StreamingMessage
                            content={msg.text}
                            isStreaming={true}
                            isUser={false}
                            isAtBottom={isAtBottomRef.current}
                            onHeightGrow={handleStreamingHeightGrow}
                            renderOptions={{
                              handleReaction: handleReaction,
                              userId: userId
                            }}
                          />
                        </div>
                      );
                    } else {
                      // Use existing MessageItem for non-streaming messages
                      return (
                        <MessageItem 
                          key={msg.id || `msg-${index}`}
                          msg={msg} 
                          handleReaction={handleReaction} 
                          userId={userId}
                        />
                      );
                    }
                  })}
                </div>
                
                {/* Bottom padding for safe area */}
                <div style={{ height: safeArea.bottom }} aria-hidden="true" />
              </div>
              
              {/* Error Display */}
              <AnimatePresence>
                {(conversationsError || uiState.error) && (
                  <motion.div 
                    className="text-red-400 text-center text-sm p-4 bg-red-900/20 backdrop-filter backdrop-blur-sm rounded-lg border border-red-500/20"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {conversationsError || uiState.error}
                  </motion.div>
                )}
              </AnimatePresence>
              
              <ScrollToBottomButton 
                show={uiState.showScrollButton} 
                onClick={scrollToBottom} 
                isMobile={isMobile}
                style={isMobile ? getFloatingButtonStyles('bottom-right', { offset: 90 }) : undefined}
              />
            </div>
          </div>

          {/* Message Input Area */}
          <motion.div
            className={`${!isMobile ? "sticky bottom-0 left-0 right-0" : ""} ${
              isMobile ? 'bg-slate-900/90' : 'bg-slate-900/50'
            } backdrop-filter backdrop-blur-md ${isInputCollapsed && isMobile ? '' : 'border-t border-white/10'}`}
            style={isMobile ? {
              ...getMobileInputStyles({ additionalPadding: isInputCollapsed ? 0 : 8 }),
              boxShadow: '0 -10px 25px -5px rgba(0, 0, 0, 0.1)'
            } : {
              boxShadow: '0 -10px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            animate={{
              height: isInputCollapsed && isMobile ? "56px" : "auto"
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* Collapsed State - Mobile Only */}
            {isMobile && isInputCollapsed && (
              <motion.button
                onClick={toggleInputCollapse}
                className="w-full h-14 flex items-center justify-center text-emerald-400/60 hover:text-emerald-300 transition-all"
                whileTap={{ scale: 0.95 }}
              >
                <MessageSquare className="w-6 h-6" />
              </motion.button>
            )}
            
            {/* Expanded Content */}
            <motion.div
              className={`${isInputCollapsed && isMobile ? "hidden" : ""} ${isMobile ? 'p-0' : 'p-4'} relative`}
              animate={{
                opacity: isInputCollapsed && isMobile ? 0 : 1
              }}
              transition={{ duration: 0.2 }}
            >
              <div className={`${isMobile ? 'w-full' : 'max-w-3xl mx-auto'}`}>
                {/* Centered Collapse Button - Mobile Only */}
                {isMobile && (
                  <motion.button
                    onClick={toggleInputCollapse}
                    className="w-full flex items-center justify-center py-1 mb-2 text-emerald-400/50 hover:text-emerald-300 transition-colors"
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      animate={{ rotate: isInputCollapsed ? 0 : 180 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </motion.div>
                  </motion.button>
                )}
                
                <MessageInput
                  onSendMessage={isFirstMessage ? originalHandleFirstMessage : handleNewMessage}
                  isInitial={isCompact || isFirstMessage}
                  disabled={
                    // LOCAL DEV FIX: Skip conversationsLoading check in development
                    // REVERT THIS if production has any issues with MessageInput being disabled
                    // TO REVERT: Change back to just "conversationsLoading" instead of "(process.env.NODE_ENV === 'development' ? false : conversationsLoading)"
                    (userId !== 'OWB1yZAffucGIyBVOe6X8lL83Z63' &&
                     userId !== 'CwRxFyFW7UfahRYNuU4jIdRj8Pr1' &&
                     (uiState.isLoading || uiState.isTransitioning ||
                      (process.env.NODE_ENV === 'development' ? false : conversationsLoading) ||
                      isStreamingRef.current || isStreaming || usageLimiter.canSendMessage === false))
                  }
                  tier={tier}
                  onSuggestionClick={handleSuggestionClick}
                  isMobile={isMobile}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
  
      {/* Mobile Chat History Panel */}
      <AnimatePresence>
        {showMobileStats && (
          <MobileStatsPanel
              userId={userId}
              onClose={closeMobileStats}
              onNewChat={() => {
                handleNewChatTest();
                closeMobileStats();
              }}
              conversations={conversations}
              activeConversationId={effectiveActiveConversation?.id}
              onChatSelect={handleChatSelect}
          />
        )}
      </AnimatePresence>
  
      {/* Upgrade Notice */}
      <AnimatePresence>
        {uiState.showUpgradePrompt && (
          <UpgradeNotice 
            isMobile={isMobile}
            tier={usageLimiter.tier}
            messageCount={usageLimiter.messageCount}
            dailyLimit={usageLimiter.tier === 'pro' ? 40 : 3}
            hoursUntilReset={
              usageLimiter.nextResetTime 
                ? (usageLimiter.nextResetTime - new Date()) / (1000 * 60 * 60)
                : null
            }
            onClose={() => updateUIState({ showUpgradePrompt: false })}
          />
        )}
      </AnimatePresence>

      <GamePredictionModal
        predictionState={predictionState}
        onSportSelect={(sport, stateUpdates = {}) => {
          if (!sport && stateUpdates) {
            updatePredictionState({
              ...stateUpdates
            });
            return;
          }

          updatePredictionState({ 
            selectedSport: sport,
            showSportSelector: false,
            step: 1,
            ...stateUpdates
          });
          
          const skipIncrementCheck = predictionState.paidWithStarCoins;
          runAutomatedGamePrediction(sport, { skipIncrementCheck });
        }}
        onClose={() => {
          updatePredictionState({
            showModal: false,
            selectedSport: null,
            step: 0,
            results: null,
            isRunning: false,
            paidWithStarCoins: false
          });
        }}
        onUpgrade={() => {
          updateUIState({ showUpgradePrompt: true });
        }}
        isBasicUser={tier === 'basic'}
        remainingSpins={gameUsageState.remaining}
        resetTime={gameUsageState.resetTime}
        userId={userId}
      />

      {/* Floating Prompt Assistant - Desktop only */}
      {!isMobile && (
        <FloatingPromptAssistant
          userId={userId}
          onUsePrompt={handleSuggestionClick}
          onUpgrade={() => updateUIState({ showUpgradePrompt: true })}
          gameInfo={null}
          isMobile={isMobile}
          messages={messages}
          isVisible={floatingPromptVisible}
          onClose={handleFloatingPromptClose}
          // Pass custom prompt functionality
          customPrompts={usageLimiter}
          starCoins={usageLimiter.starCoins}
          tier={tier}
        />
      )}

<MessageLimitModal
  isVisible={showMessageLimitWarning}
  messageCount={warningMessageCount}
  onContinue={handleMessageLimitContinue}
  onNewChat={handleMessageLimitNewChat}
  onClose={handleMessageLimitClose}
/>

      {/* CSS Styles (restored from old version) */}
      <style dangerouslySetInnerHTML={{ __html: chatWindowStyles }} />

      {/* Chat Onboarding Tour - Shows on first visit for authenticated users */}
      <ChatOnboardingTour
        ref={chatOnboardingRef} // Ref for manual trigger via help button
        userId={userId}
        autoStart={false}
        onComplete={() => console.log('Chat onboarding completed')}
        onSkip={() => console.log('Chat onboarding skipped')}
      />
    </div>
    </>
  );
});

ChatWindow.displayName = 'ChatWindow';

// Export both the component and context for use in other components
export { ChatContext, MessageLimitModal };
export default ChatWindow;